import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  BarChart3, 
  Trash2, 
  Filter, 
  Zap, 
  Shield, 
  Clock,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Database,
  Globe
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: BarChart3,
      title: 'GA4 Analytics Integration',
      description: 'Connect with Google Analytics 4 to fetch real-time traffic data for all your WordPress posts.',
      gradient: 'from-cyan-500 to-blue-500',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'
    },
    {
      icon: Filter,
      title: 'Smart Filtering',
      description: 'Filter posts by publication age, traffic levels, and date ranges. Find exactly what needs cleanup.',
      gradient: 'from-purple-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1643834004015-243c2f98e490?w=800&q=80'
    },
    {
      icon: Trash2,
      title: 'Bulk Delete Actions',
      description: 'Select multiple low-performing posts and delete them in one click. Clean up thousands of posts effortlessly.',
      gradient: 'from-orange-500 to-red-500',
      image: 'https://images.unsplash.com/photo-1614189249054-df6749c95f4d?w=800&q=80'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized performance handles sites with 10,000+ posts. Load, filter, and delete at blazing speeds.',
      gradient: 'from-yellow-500 to-orange-500',
      image: 'https://images.unsplash.com/photo-1754348523191-6705920cdb4a?w=800&q=80'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your WordPress and GA4 credentials are encrypted. We never store your content or analytics data.',
      gradient: 'from-green-500 to-emerald-500',
      image: 'https://images.unsplash.com/photo-1704206942171-a492308ca218?w=800&q=80'
    },
    {
      icon: Clock,
      title: 'Save Hours Weekly',
      description: 'Automate what used to take days. Identify and remove underperforming content in minutes.',
      gradient: 'from-indigo-500 to-purple-500',
      image: 'https://images.unsplash.com/photo-1664526936810-ec0856d31b92?w=800&q=80'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Connect Your Accounts',
      description: 'Link your WordPress site and Google Analytics 4 property in seconds.',
      icon: Globe
    },
    {
      number: '02',
      title: 'Analyze Your Content',
      description: 'View all posts with traffic data. Filter by age, sessions, and performance metrics.',
      icon: TrendingUp
    },
    {
      number: '03',
      title: 'Clean Up & Optimize',
      description: 'Select low-traffic posts and bulk delete them. Watch your site performance improve.',
      icon: Database
    }
  ];

  const stats = [
    { value: '10K+', label: 'Posts Managed' },
    { value: '500+', label: 'Sites Cleaned' },
    { value: '99.9%', label: 'Uptime' },
    { value: '<2s', label: 'Load Time' }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative">
      {/* Animated grid background */}
      <div className="fixed inset-0 opacity-20">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0, 240, 255, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-30 pointer-events-none"></div>

      {/* Header */}
      <nav className="relative z-50 border-b border-white/10 backdrop-blur-md bg-black/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2"
            >
              <div className="relative">
                <Sparkles className="w-6 h-6 text-cyan-400 animate-pulse" />
                <div className="absolute inset-0 bg-cyan-400 blur-xl opacity-50"></div>
              </div>
              <span className="font-['Unbounded'] text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                WP Magica
              </span>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex gap-3 items-center"
            >
              <Button
                onClick={() => navigate('/pricing')}
                variant="ghost"
                className="text-white hover:text-cyan-400 transition-colors"
              >
                Pricing
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                className="text-white hover:text-cyan-400 transition-colors"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-black hover:from-cyan-400 hover:to-blue-400 rounded-full font-bold shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] transition-all border-0"
              >
                Get Started Free
              </Button>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Hero background image */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1578070581071-d9b52bf80993?w=1920&q=80')`,
              filter: 'blur(2px)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            <motion.span 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block px-6 py-3 mb-8 text-sm font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/50 rounded-full bg-cyan-500/10 backdrop-blur-sm shadow-[0_0_20px_rgba(6,182,212,0.3)]"
            >
              ⚡ Powered by GA4 + WordPress API
            </motion.span>
            
            <h1 className="font-['Unbounded'] text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">
                The Control Room
              </span>
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                for Your Content
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto font-light">
              Analyze <span className="text-cyan-400 font-semibold">10,000+ WordPress posts</span> in seconds. 
              Identify low-traffic content with <span className="text-purple-400 font-semibold">GA4 data</span>. 
              Delete underperformers in one click.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Button
                onClick={() => navigate('/signup')}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 rounded-full font-bold text-lg px-8 py-6 shadow-[0_0_40px_rgba(6,182,212,0.5)] hover:shadow-[0_0_60px_rgba(6,182,212,0.7)] transition-all group border-0 relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  Start Cleaning Now
                  <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Button>
              <Button
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-cyan-400 rounded-full font-bold text-lg px-8 py-6 transition-all backdrop-blur-sm"
              >
                See How It Works
              </Button>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-['Unbounded'] font-black bg-gradient-to-br from-cyan-400 to-purple-400 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-12 flex items-center justify-center gap-8 text-sm text-slate-400 flex-wrap"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                No credit card required
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                7-day free trial
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Enterprise-grade security
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-4 block">
              Features
            </span>
            <h2 className="font-['Unbounded'] text-4xl md:text-6xl font-bold tracking-tight mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Everything You Need
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Purpose-built for content managers handling massive WordPress sites
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-cyan-500/50 transition-all duration-500"
                >
                  {/* Background image */}
                  <div 
                    className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url('${feature.image}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-br ${feature.gradient} transition-opacity duration-500`}></div>
                  
                  <div className="relative p-8">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} p-0.5 mb-6 group-hover:scale-110 transition-transform duration-500`}>
                      <div className="w-full h-full bg-[#0A0A0A] rounded-2xl flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed text-base">
                      {feature.description}
                    </p>
                  </div>
                  
                  {/* Glow effect */}
                  <div className={`absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 bg-gradient-to-br ${feature.gradient} blur-xl transition-opacity duration-500 -z-10`}></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-4 block">
              Process
            </span>
            <h2 className="font-['Unbounded'] text-4xl md:text-6xl font-bold tracking-tight mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                How It Works
              </span>
            </h2>
            <p className="text-xl text-slate-400">
              Get started in 3 simple steps
            </p>
          </motion.div>

          <div className="space-y-16">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="flex flex-col md:flex-row gap-8 items-center"
                >
                  <div className="flex-shrink-0 relative">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center font-['JetBrains_Mono'] text-3xl font-bold relative z-10">
                      {step.number}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                  </div>
                  
                  <div className="flex-1 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-cyan-500/50 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-cyan-500/20 to-purple-500/20 p-4 rounded-xl">
                        <Icon className="w-8 h-8 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                        <p className="text-lg text-slate-400">{step.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 mb-6 text-sm font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/10">
              Simple Pricing
            </span>
            <h2 className="font-['Unbounded'] text-4xl md:text-5xl font-black tracking-tighter mb-6">
              <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                Start Free, Upgrade When Ready
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm"
            >
              <h3 className="text-2xl font-bold text-white mb-2">Free Trial</h3>
              <p className="text-slate-400 mb-6">Test the waters</p>
              <div className="mb-8">
                <span className="text-5xl font-black text-white">$0</span>
              </div>
              <ul className="space-y-3 mb-8 text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  50 posts per session
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  2 refreshes per day
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  Delete 10 posts at once
                </li>
              </ul>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Start Free
              </Button>
            </motion.div>

            {/* Pro Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative p-8 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 backdrop-blur-sm"
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-cyan-500 to-purple-500 text-black px-4 py-1 rounded-full text-sm font-bold">
                  Most Popular
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
              <p className="text-slate-400 mb-6">For serious cleanups</p>
              <div className="mb-8">
                <span className="text-5xl font-black bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">$9.99</span>
                <span className="text-slate-400">/month</span>
              </div>
              <ul className="space-y-3 mb-8 text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  10,000 posts per session
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  Unlimited refreshes
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  Delete 500 posts at once
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  Advanced sorting & filtering
                </li>
              </ul>
              <Button
                onClick={() => navigate('/signup?plan=pro')}
                className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white hover:from-cyan-400 hover:to-purple-400 border-0"
              >
                Get Pro Plan
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1657727534685-36b09f84e193?w=1920&q=80')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Unbounded'] text-4xl md:text-6xl font-black tracking-tighter mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">
                Ready to Clean Up
              </span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Your WordPress Site?
              </span>
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Join content managers who've deleted <span className="text-cyan-400 font-bold">millions</span> of low-traffic posts and improved site performance.
            </p>
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 rounded-full font-bold text-xl px-12 py-7 shadow-[0_0_60px_rgba(6,182,212,0.6)] hover:shadow-[0_0_80px_rgba(6,182,212,0.8)] transition-all group border-0"
            >
              Get Started Free
              <Sparkles className="ml-2 w-6 h-6 group-hover:rotate-12 transition-transform" />
            </Button>
            <p className="mt-8 text-slate-500">
              No credit card required • 7-day free trial • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Sparkles className="w-6 h-6 text-cyan-400" />
                <div className="absolute inset-0 bg-cyan-400 blur-lg opacity-50"></div>
              </div>
              <span className="font-['Unbounded'] font-bold text-xl bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                WP Magica
              </span>
            </div>
            <div className="flex gap-8 text-sm text-slate-400">
              <a href="#features" className="hover:text-cyan-400 transition-colors">Features</a>
              <a href="#pricing" className="hover:text-cyan-400 transition-colors">Pricing</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Privacy</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Terms</a>
            </div>
            <p className="text-sm text-slate-500">
              © 2025 WP Magica. Built with FastAPI, React & MongoDB.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
